"use client";

import { MultiDietGuideDescCards } from "./";
import { MultiDietGuideStepsList } from "./";
import { MultiDietGuideType } from "@/features/static-builder/types/multidiet-guide.type";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import React, { useRef } from "react";

gsap.registerPlugin(ScrollTrigger);

export const MultiDietGuideDesktop = ({
  data,
  videoUrls,
}: {
  data: MultiDietGuideType;
  videoUrls: string[];
}) => {
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const descriptionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(() => {
    const videos = videoRefs.current;
    const steps = stepRefs.current;
    const descriptions = descriptionRefs.current;

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: "#steps",
        start: "top 20%",
        end: "+=2000",
        scrub: 0.5,
        pin: true,
      },
    });

    timeline.fromTo(
      "#videos-container",
      { scale: 0.8, y: -50 },
      { scale: 1, y: 0, duration: 0.8, ease: "power1.inOut" }
    );
    data.guideSteps.forEach((_, idx) => {
      const stepEl = steps[idx];
      const videoEl = videos[idx];
      const descEl = descriptions[idx];
      const prevDescEl = descriptions[idx - 1];

      if (!stepEl || !videoEl || !descEl) return;
      const label = `step-${idx}`;

      timeline.addLabel(label);

      timeline.fromTo(
        stepEl,
        { opacity: 0, y: 150 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power1.inOut" }
      );

      timeline.to(stepEl, {
        height: 88,
        duration: 0.6,
        delay: 0.3,
        ease: "power1.inOut",
      });

      if (prevDescEl) {
        timeline.to(
          prevDescEl,
          {
            opacity: 0,
            y: -100,
            duration: 0.6,
            ease: "power1.inOut",
          },
          label
        );
      }
      timeline.fromTo(
        descEl,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power1.inOut" },
        label
      );

      if (idx !== 0) {
        timeline.fromTo(
          videoEl,
          { yPercent: 100 },
          { yPercent: 0, duration: 1, ease: "power1.inOut" },
          label
        );
      }
    });
  }, []);

  return (
    <div
      id="steps"
      className="hidden lg:flex justify-between lg:gap-4 xl:gap-10"
    >
      <div className="flex flex-col gap-6 items-start lg:flex-1 xl:flex-[1.3]">
        <MultiDietGuideStepsList data={data} ref={stepRefs} />
      </div>
      <div
        id="videos-container"
        className="relative flex-1 max-h-[520px] overflow-hidden rounded-2xl"
      >
        {data.videos.map((video, idx) => (
          <video
            key={video.id}
            ref={(el: HTMLVideoElement | null) => {
              videoRefs.current[idx] = el;
            }}
            controls
            autoPlay
            muted
            playsInline
            className="absolute inset-0 w-full h-full aspect-video object-contain rounded-2xl z-0"
            src={`${videoUrls[idx]}`}
          />
        ))}
        <div className="absolute inset-0 w-full h-full z-10 border-8 border-brand-grey1 bg-transparent rounded-2xl pointer-events-none" />
      </div>
      <div className="lg:flex-1 xl:flex-[1.5] relative">
        <MultiDietGuideDescCards data={data} ref={descriptionRefs} />
      </div>
    </div>
  );
};
